import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import ButtonNB from './ui-nb/ButtonNB';
import BadgeNB from './ui-nb/BadgeNB';
import {
  getCurrentIndustryIndex,
  getIndustryExample,
  onIndustryChange,
  localize,
  type DemoData,
} from '../data/landing-examples';

type TrackState = 'idle' | 'loading' | 'result' | 'notFound';

export default function TrackDemoNB() {
  const { t, language } = useTranslation();
  const { theme, colors } = useNbTheme();
  const isDark = theme === 'dark';
  const inputRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState('');
  const [state, setState] = useState<TrackState>('idle');
  const [demoResult, setDemoResult] = useState<DemoData | null>(null);

  // Keep in sync with the dashboard carousel
  useEffect(() => {
    const unsub = onIndustryChange(() => {
      // If user is viewing a result, refresh it on industry change
      if (state === 'result') {
        const idx = getCurrentIndustryIndex();
        const example = getIndustryExample(idx);
        setDemoResult(example.demo);
      }
    });
    return unsub;
  }, [state]);

  const handleTrack = () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setState('loading');

    setTimeout(() => {
      if (trimmed.toUpperCase() === 'DEMO') {
        const idx = getCurrentIndustryIndex();
        const example = getIndustryExample(idx);
        setDemoResult(example.demo);
        setState('result');
      } else {
        setState('notFound');
      }
    }, 800);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTrack();
  };

  const handleReset = () => {
    setCode('');
    setDemoResult(null);
    setState('idle');
    inputRef.current?.focus();
  };

  const cardStyle = {
    background: colors.bgCard,
    border: `3px solid ${colors.textPrimary}`,
    borderRadius: '2px',
    maxWidth: '540px',
    margin: '0 auto',
  };

  return (
    <section
      id="track-demo"
      className="py-12 md:py-20"
      style={{ background: colors.bgSection || '#F0F0F0' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-8 md:mb-10">
          <h2
            className="font-nb font-bold mb-3"
            style={{
              color: colors.textPrimary,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.2,
            }}
          >
            {t('trackDemo.title')}
          </h2>
          <p
            className="font-nb max-w-lg mx-auto"
            style={{
              color: colors.textSecondary,
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
              lineHeight: 1.6,
            }}
          >
            {t('trackDemo.subtitle')}
          </p>
        </div>

        {/* Widget card */}
        <div style={cardStyle} className="p-5 md:p-8">
          {/* Input row */}
          {state !== 'result' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-stretch">
                <span
                  className="font-nb-mono font-bold text-sm flex items-center px-3 border-3 border-r-0"
                  style={{
                    color: colors.textMuted,
                    background: colors.bgSection || '#F0F0F0',
                    borderColor: colors.textPrimary,
                  }}
                >
                  {demoResult?.code?.split('-')[0] || t('trackDemo.code')}-
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (state === 'notFound') setState('idle');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={t('trackDemo.inputPlaceholder')}
                  disabled={state === 'loading'}
                  className="flex-1 font-nb-mono text-sm px-3 py-3 border-3 focus:outline-none min-w-0"
                  style={{
                    background: colors.bgCard,
                    borderColor:
                      state === 'notFound' ? '#D64545' : colors.textPrimary,
                    color: colors.textPrimary,
                    transition: 'border-color 200ms ease',
                  }}
                />
              </div>
              <ButtonNB
                variant="primary"
                size="lg"
                onClick={handleTrack}
                disabled={state === 'loading' || !code.trim()}
              >
                {state === 'loading'
                  ? t('trackDemo.loading')
                  : t('trackDemo.cta')}
              </ButtonNB>
            </div>
          )}

          {/* Not found */}
          {state === 'notFound' && (
            <div
              className="mt-4 p-4 border-3"
              style={{
                borderColor: '#D64545',
                background: isDark ? 'rgba(214,69,69,0.08)' : '#FFF5F5',
                borderRadius: '2px',
              }}
            >
              <p className="font-nb text-sm" style={{ color: '#D64545' }}>
                {t('trackDemo.notFound')}
              </p>
            </div>
          )}

          {/* Result */}
          {state === 'result' && demoResult && (
            <div>
              {/* Status header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span
                    className="font-nb-mono font-bold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {demoResult.code}
                  </span>
                  <BadgeNB variant="warning">
                    {localize(demoResult.status, language)}
                  </BadgeNB>
                </div>
                <button
                  onClick={handleReset}
                  className="font-nb text-xs font-bold hover:underline"
                  style={{ color: colors.textSecondary }}
                >
                  &larr; {t('trackDemo.inputPlaceholder')}
                </button>
              </div>

              {/* Details grid */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4"
                style={{
                  padding: '1rem',
                  background: colors.bgSection || '#F0F0F0',
                  border: `2px solid ${colors.textPrimary}`,
                }}
              >
                <div>
                  <span
                    className="font-nb text-xs block mb-0.5"
                    style={{ color: colors.textMuted }}
                  >
                    Device
                  </span>
                  <span
                    className="font-nb font-bold text-sm"
                    style={{ color: colors.textPrimary }}
                  >
                    {localize(demoResult.device, language)}
                  </span>
                </div>
                <div>
                  <span
                    className="font-nb text-xs block mb-0.5"
                    style={{ color: colors.textMuted }}
                  >
                    Received
                  </span>
                  <span
                    className="font-nb font-bold text-sm"
                    style={{ color: colors.textPrimary }}
                  >
                    {demoResult.received}
                  </span>
                </div>
                <div>
                  <span
                    className="font-nb text-xs block mb-0.5"
                    style={{ color: colors.textMuted }}
                  >
                    Estimated delivery
                  </span>
                  <span
                    className="font-nb font-bold text-sm"
                    style={{ color: colors.textPrimary }}
                  >
                    {demoResult.estimated}
                  </span>
                </div>
                <div>
                  <span
                    className="font-nb text-xs block mb-0.5"
                    style={{ color: colors.textMuted }}
                  >
                    Technician
                  </span>
                  <span
                    className="font-nb font-bold text-sm"
                    style={{ color: colors.textPrimary }}
                  >
                    {demoResult.technician}
                  </span>
                </div>
              </div>

              {/* Note */}
              <div
                className="p-3 border-3"
                style={{
                  borderColor: colors.accentPrimary || '#FFD600',
                  background: isDark ? 'rgba(255,214,0,0.08)' : '#FFFDE7',
                }}
              >
                <span
                  className="font-nb text-xs font-bold mr-2"
                  style={{ color: isDark ? '#E8E8E8' : '#1C1C1C' }}
                >
                  NOTE:
                </span>
                <span
                  className="font-nb text-xs"
                  style={{ color: colors.textSecondary }}
                >
                  {localize(demoResult.note, language)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Trust note */}
        <p
          className="font-nb text-xs text-center mt-4"
          style={{ color: colors.textMuted }}
        >
          {t('dashboardPreview.mockNote')}
        </p>
      </div>
    </section>
  );
}
